import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

import {
	Badge,
	Box,
	Flex,
	Group,
	LoadingOverlay,
	Pagination,
	ScrollArea,
	Select,
	Table,
	Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';

import { useQuery } from '@tanstack/react-query';

import { bookingApi } from '@/shared/api/booking';
import { bookingObjectsApi } from '@/shared/api/bookingObjects';
import { personApi } from '@/shared/api/person';
import { chunk, isActiveGuest } from '@/shared/helpers/utils';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

type BookingType = 'location';

const AllBookings = () => {
	const [activePage, setPage] = useState(1);
	const [period, setPeriod] = useState<[Date | null, Date | null]>([new Date(), new Date()]);
	const [personId, setPersonId] = useState<number | null>();
	const [locationId, setLocationId] = useState<number | null>();
	const [objectId, setObjectId] = useState<number | null>();

	const { data: allBookings, isLoading: isAllBookingsLoading } = useQuery({
		queryKey: ['AllBookings', { period }],
		queryFn: () =>
			bookingApi
				.getAllBookingsByDate(
					dayjs(period[0]).startOf('day').toDate(),
					dayjs(period[1]).endOf('day').toDate(),
				)
				.then((resp) => {
					return resp.objects;
				}),
		staleTime: 300_000,
		enabled: period.every((date) => date),
	});

	const { data: persons } = useQuery({
		queryKey: ['persons'],
		queryFn: () => personApi.getPersons(0, 100000),
		staleTime: 300_000,
	});

	const { data: objects } = useQuery({
		queryKey: ['objects'],
		queryFn: () => bookingObjectsApi.getAllObjects(),
		staleTime: 300_000,
	});

	const hasGuests = useMemo(
		() => allBookings?.some((booking) => booking.guests?.some(isActiveGuest)) ?? false,
		[allBookings],
	);

	const chunked = chunk(
		allBookings?.filter((elem) => {
			if (personId || locationId) {
				if (personId && locationId && objectId) {
					return (
						elem.initiator_id === personId &&
						elem.booking_object?.parent_id === locationId &&
						elem.booking_object_id === objectId
					);
				}

				if (locationId && objectId) {
					return (
						elem.booking_object?.parent_id === locationId && elem.booking_object_id === objectId
					);
				}

				if (personId && locationId) {
					return elem.initiator_id === personId && elem.booking_object?.parent_id === locationId;
				}

				return elem.initiator_id === personId || elem.booking_object?.parent_id === locationId;
			}

			return true;
		}) ?? [],
		10,
	);

	const rows = chunked[activePage - 1]?.map((booking) => (
		<Table.Tr key={booking.id}>
			<Table.Td>
				<Text size="md" fw={600} lts="normal">
					{booking.client?.full_name ?? 'Нет данных'}
				</Text>
			</Table.Td>
			{hasGuests &&
				(() => {
					const activeGuests = booking.guests?.filter(isActiveGuest) ?? [];

					return (
						<>
							<Table.Td>
								{activeGuests.length ? (
									<Flex direction="column" gap={4}>
										{activeGuests.map((guest) => (
											<Flex key={guest.id ?? guest.full_name} direction="column">
												<Text size="md">{guest.full_name}</Text>
												<Text size="sm" c="dimmed">
													{guest.email}
												</Text>
												<Text size="sm" c="dimmed">
													{guest.phone}
												</Text>
											</Flex>
										))}
									</Flex>
								) : (
									<Text size="md" c="dimmed">
										—
									</Text>
								)}
							</Table.Td>
							<Table.Td>
								{activeGuests.length ? (
									<Flex direction="column" gap={4}>
										{activeGuests.map((guest) => (
											<Text key={guest.id ?? guest.full_name} size="md">
												{guest.renter?.renter_name || '—'}
											</Text>
										))}
									</Flex>
								) : (
									<Text size="md" c="dimmed">
										—
									</Text>
								)}
							</Table.Td>
						</>
					);
				})()}
			<Table.Td>
				<Text size="md" c="dimmed">
					{booking.booking_object?.parent?.name ?? booking.booking_object.name}
				</Text>
			</Table.Td>
			<Table.Td>
				<Text size="md" c="dimmed">
					{booking.booking_object.name.replace(/(?=\s\|).*/gm, '')}
				</Text>
			</Table.Td>
			<Table.Td ta="center">
				{booking.car_number && (
					<Badge variant="light">
						<Text size="md" fw={600}>
							{booking.car_number}
						</Text>
					</Badge>
				)}
			</Table.Td>
			<Table.Td ta="center">
				<Text size="md" c="dimmed">
					{dayjs(booking.start).format('DD MMMM YYYY HH:mm')}
				</Text>
			</Table.Td>
			<Table.Td ta="center">
				<Text size="md" c="dimmed">
					{dayjs(booking.end).format('DD MMMM YYYY HH:mm')}
				</Text>
			</Table.Td>
		</Table.Tr>
	));

	const locationOptions = useMemo(
		() =>
			objects
				?.filter((location) => (location?.type?.kind as BookingType) === 'location')
				.map((elem) => ({
					value: `${elem?.id}`,
					label: `${elem?.name}`,
				})),
		[objects],
	);
	const objectOptions = useMemo(
		() =>
			objects
				?.filter((object) => object?.parent_id && object?.parent_id === locationId)
				.map((elem) => ({
					value: `${elem.id}`,
					label: `${elem.name}`,
				})),
		[locationId, objectId],
	);

	useEffect(() => {
		if (chunked.length < activePage) {
			setPage(chunked.length || 1);
		}
	}, [allBookings, period, locationId, objectId, personId]);

	return (
		<LayoutRow
			title={{
				title: { children: 'Все бронирования', size: 'h4', fw: 800 },
			}}
			type="once"
			style={{ height: 'calc(100vh - 116px)' }}
		>
			<Box pos="relative" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
				<LoadingOverlay
					visible={isAllBookingsLoading}
					zIndex={1000}
					overlayProps={{ radius: 2, blur: 2 }}
				/>
				<Group>
					<DatePickerInput
						w={240}
						radius="lg"
						placeholder="Период"
						valueFormat="DD MMM YYYY"
						type="range"
						value={period}
						onChange={setPeriod}
						allowSingleDateInRange
						highlightToday
						clearable
					/>

					<Select
						radius="lg"
						placeholder="Сотрудник"
						allowDeselect={false}
						onChange={(value) => setPersonId(Number(value))}
						data={persons?.map((elem) => {
							return { value: `${elem.id}`, label: elem.full_name };
						})}
						limit={30}
						w={240}
						searchable
						clearable
					/>

					<Select
						radius="lg"
						placeholder="Локация"
						allowDeselect={false}
						onChange={(value) => {
							setLocationId(Number(value));
							setObjectId(null);
						}}
						data={locationOptions}
						limit={100}
						w={240}
						searchable
						clearable
					/>

					<Select
						radius="lg"
						placeholder="Место"
						allowDeselect={false}
						onChange={(value) => setObjectId(Number(value))}
						data={objectOptions}
						value={String(objectId || '')}
						limit={100}
						w={240}
						searchable
						clearable
					/>
				</Group>
				{allBookings?.length ? (
					<>
						<ScrollArea h="100dvh" pr="md" scrollbarSize={12} offsetScrollbars>
							<Table verticalSpacing="md" stickyHeader>
								<Table.Thead>
									<Table.Tr>
										<Table.Th>
											<Text size="md" c="dimmed">
												Клиент
											</Text>
										</Table.Th>
										{hasGuests && (
											<>
												<Table.Th>
													<Text size="md" c="dimmed">
														Гость
													</Text>
												</Table.Th>
												<Table.Th>
													<Text size="md" c="dimmed">
														Арендатор
													</Text>
												</Table.Th>
											</>
										)}
										<Table.Th>
											<Text size="md" c="dimmed">
												Локация
											</Text>
										</Table.Th>
										<Table.Th>
											<Text size="md" c="dimmed">
												Место
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Номер авто
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Начало
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Конец
											</Text>
										</Table.Th>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>{rows}</Table.Tbody>
							</Table>
						</ScrollArea>
						<Pagination total={chunked.length} value={activePage} onChange={setPage} mt="sm" />
					</>
				) : (
					<Flex direction="column" align="center" justify="center" h="calc(100vh - 300px)">
						<Text fz={18} fw={700}>
							Пока ничего нет 😔
						</Text>
					</Flex>
				)}
			</Box>
		</LayoutRow>
	);
};
export default AllBookings;
